import { memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const TOTAL_KEY = '__stackTotal';
const AXIS_TICK_STYLE = { fontSize: 11, fill: '#6B7280' };
const CATEGORY_SLOT = 20;
const MAX_VISIBLE_CATEGORIES = 50;
const SCROLL_AXIS_HEIGHT = 128;
const Y_AXIS_WIDTH = 64;
const CHART_TOP_MARGIN = 24;
const CHART_BOTTOM_MARGIN = 8;
const AXIS_LINE_COLOR = '#9CA3AF';

const defaultFormatter = value => value;

const formatAxisTick = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return Number.isInteger(numeric) ? String(numeric) : String(numeric);
};

/**
 * Fixed left scale for scrollable charts — Recharts YAxis ticks were getting
 * clipped in the narrow sticky strip, so numbers are drawn here directly.
 */
const FixedYAxisScale = memo(
  ({
    height,
    axisHeight,
    yTicks = [],
    yMax = 0,
    label = '',
  }) => {
    const plotTop = CHART_TOP_MARGIN;
    const plotBottom = height - CHART_BOTTOM_MARGIN - axisHeight;
    const plotHeight = Math.max(plotBottom - plotTop, 1);
    const max = yMax > 0 ? yMax : 1;
    const ticks = yTicks.length ? yTicks : [0, max];

    return (
      <Box
        sx={{
          width: Y_AXIS_WIDTH,
          height,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {label ? (
          <Typography
            sx={{
              position: 'absolute',
              left: 0,
              top: plotTop + plotHeight / 2,
              transform: 'translateY(-50%) rotate(-90deg)',
              transformOrigin: 'center',
              fontSize: 11,
              color: '#6B7280',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {label}
          </Typography>
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            top: plotTop,
            height: plotHeight,
            right: 0,
            width: 0,
            borderRight: `1px solid ${AXIS_LINE_COLOR}`,
          }}
        />
        {ticks.map(tick => {
          const ratio = Number(tick) / max;
          const top = plotTop + (1 - ratio) * plotHeight;
          return (
            <Box
              key={`y-tick-${tick}`}
              sx={{
                position: 'absolute',
                top,
                right: 0,
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: '#6B7280',
                  lineHeight: 1,
                  textAlign: 'right',
                  minWidth: 24,
                }}
              >
                {formatAxisTick(tick)}
              </Typography>
              <Box
                sx={{
                  width: 6,
                  height: 0,
                  borderTop: `1px solid ${AXIS_LINE_COLOR}`,
                }}
              />
            </Box>
          );
        })}
      </Box>
    );
  }
);

FixedYAxisScale.displayName = 'FixedYAxisScale';

const truncateAxisLabel = (value, max = 12) => {
  const text = value == null ? '' : String(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
};

const AngledTick = ({ x, y, payload }) => (
  <text
    x={x}
    y={y + 8}
    textAnchor="end"
    transform={`rotate(-45, ${x}, ${y + 8})`}
    fontSize={11}
    fontStyle="italic"
    fill="#374151"
  >
    <title>{payload?.value}</title>
    {payload?.value}
  </text>
);

const VerticalTick = ({ x, y, payload }) => {
  // Nudge left so the rotated name lines up under the bar center.
  const tickX = x - 6;
  const tickY = y + 10;
  return (
    <text
      x={tickX}
      y={tickY}
      textAnchor="end"
      transform={`rotate(-90, ${tickX}, ${tickY})`}
      fontSize={11}
      fill="#374151"
    >
      <title>{payload?.value}</title>
      {truncateAxisLabel(payload?.value, 14)}
    </text>
  );
};

const legendFormatter = value => (
  <span style={{ color: '#374151', fontSize: 14 }}>{value}</span>
);

const TOOLTIP_SOURCE_KEY = {
  billableHours: 'sourceBillableHours',
  nonBillableHours: 'sourceNonBillableHours',
};

/**
 * Always read hours from source* fields on the row.
 * Recharts stacked `value` (and focus-mutated series keys) can be 0 while
 * the painted bar still reflects the real API hours.
 */
const ChartTooltip = ({
  active,
  payload,
  series,
  valueFormatter,
  sourceData = [],
  xKey,
}) => {
  if (!active || !payload?.length) return null;
  const tip = payload[0]?.payload;
  if (!tip) return null;

  // Resolve against the original series data by employeeId so we never depend
  // on Recharts' stacked tooltip value (which can be 0 for some rows).
  const row =
    sourceData.find(
      item =>
        (tip.employeeId != null &&
          item.employeeId != null &&
          String(item.employeeId) === String(tip.employeeId)) ||
        (xKey && item[xKey] != null && item[xKey] === tip[xKey])
    ) ?? tip;

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: 1,
        px: 1.25,
        py: 1,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#111827', mb: 0.5 }}>
        {row.employeeName ?? row.axisLabel ?? ''}
      </Typography>
      {series.map(seriesItem => {
        const sourceKey =
          TOOLTIP_SOURCE_KEY[seriesItem.dataKey] ?? seriesItem.dataKey;
        const hours = row[sourceKey] ?? row[seriesItem.dataKey] ?? 0;
        return (
          <Typography
            key={seriesItem.dataKey}
            sx={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '2px',
                bgcolor: seriesItem.color,
                mr: 0.75,
              }}
            />
            {seriesItem.name} : {valueFormatter(hours)}
          </Typography>
        );
      })}
    </Box>
  );
};

/**
 * Stacked vertical bar chart with a total label on top of each stack.
 * `series` is bottom-to-top: [{ dataKey, name, color }].
 */
const StackedBarChart = memo(
  ({
    title = '',
    data = [],
    xKey,
    series = [],
    yAxisLabel = '',
    yTicks,
    height = 340,
    valueFormatter = defaultFormatter,
    showTotals = true,
    activeSegment = null,
    onSegmentClick,
  }) => {
    const isInteractive = typeof onSegmentClick === 'function';

    const matchesActiveRow = (entry, segment) => {
      if (!segment) return false;
      if (segment.employeeId != null && entry?.employeeId != null) {
        return String(entry.employeeId) === String(segment.employeeId);
      }
      return entry?.[xKey] === segment.xValue;
    };

    const chartData = useMemo(() => {
      const focused = data.map(item => {
        if (!activeSegment || !matchesActiveRow(item, activeSegment)) {
          return item;
        }
        const next = { ...item };
        series.forEach(seriesItem => {
          if (seriesItem.dataKey !== activeSegment.dataKey) {
            next[seriesItem.dataKey] = 0;
          }
        });
        return next;
      });
      return focused.map(item => ({
        ...item,
        [TOTAL_KEY]: series.reduce(
          (sum, seriesItem) => sum + (Number(item[seriesItem.dataKey]) || 0),
          0
        ),
      }));
    }, [data, series, activeSegment, xKey]);

    const fillFor = (entry, seriesItem) => {
      if (!activeSegment) return seriesItem.color;
      const isSelected =
        matchesActiveRow(entry, activeSegment) &&
        seriesItem.dataKey === activeSegment.dataKey;
      return isSelected
        ? seriesItem.color
        : seriesItem.mutedColor || '#D1D5DB';
    };

    const handleBarClick = (seriesItem, row) => {
      const source = row?.payload ?? row ?? {};
      const sourceKey =
        TOOLTIP_SOURCE_KEY[seriesItem.dataKey] ?? seriesItem.dataKey;
      const value =
        Number(source[sourceKey] ?? source[seriesItem.dataKey]) || 0;
      const xValue = source.employeeName ?? source[xKey];
      if (!value || !xValue) return;
      onSegmentClick({
        xValue,
        dataKey: seriesItem.dataKey,
        employeeId: source.employeeId,
      });
    };

    const yMax = yTicks?.length ? Math.max(...yTicks) : 0;
    const yDomain = yTicks?.length ? [0, yMax] : [0, 'auto'];

    const scrollable = chartData.length > MAX_VISIBLE_CATEGORIES;
    const plotWidth = Math.max(chartData.length * CATEGORY_SLOT, CATEGORY_SLOT);
    const axisHeight = scrollable ? SCROLL_AXIS_HEIGHT : 80;

    const yAxisLabelProp = yAxisLabel
      ? {
          value: yAxisLabel,
          angle: -90,
          position: 'insideLeft',
          style: { fontSize: 11, fill: '#6B7280' },
        }
      : undefined;

    const bars = series.map((s, index) => (
      <Bar
        key={s.dataKey}
        dataKey={s.dataKey}
        name={s.name}
        stackId="stack"
        fill={s.color}
        maxBarSize={18}
        cursor={isInteractive ? 'pointer' : undefined}
        onClick={isInteractive ? row => handleBarClick(s, row) : undefined}
      >
        {isInteractive &&
          chartData.map((entry, entryIndex) => (
            <Cell
              key={`${s.dataKey}-${entryIndex}`}
              fill={fillFor(entry, s)}
            />
          ))}
        {showTotals && index === series.length - 1 && (
          <LabelList
            dataKey={TOTAL_KEY}
            position="top"
            formatter={value => (Number(value) ? valueFormatter(value) : '')}
            style={{ fontSize: 11, fontWeight: 600, fill: '#111827' }}
          />
        )}
      </Bar>
    ));

    const plot = (
      <BarChart
        width={scrollable ? plotWidth : undefined}
        height={scrollable ? height : undefined}
        data={chartData}
        barCategoryGap={0}
        barGap={0}
        margin={{
          top: CHART_TOP_MARGIN,
          right: scrollable ? 16 : 8,
          bottom: CHART_BOTTOM_MARGIN,
          left: scrollable ? 8 : 8,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey={xKey}
          interval={0}
          height={axisHeight}
          tick={scrollable ? <VerticalTick /> : <AngledTick />}
          tickLine={false}
        />
        <YAxis
          hide={scrollable}
          domain={yDomain}
          ticks={yTicks}
          tick={AXIS_TICK_STYLE}
          tickLine={{ stroke: AXIS_LINE_COLOR }}
          axisLine={{ stroke: AXIS_LINE_COLOR }}
          width={scrollable ? 0 : 48}
          label={scrollable ? undefined : yAxisLabelProp}
        />
        <Tooltip
          cursor={{ fill: 'rgba(47, 120, 238, 0.06)' }}
          shared
          filterNull={false}
          content={({ active, payload }) => (
            <ChartTooltip
              active={active}
              payload={payload}
              series={series}
              valueFormatter={valueFormatter}
              sourceData={data}
              xKey={xKey}
            />
          )}
        />
        {!scrollable && (
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            iconType="square"
            formatter={legendFormatter}
            wrapperStyle={{ paddingLeft: 16 }}
          />
        )}
        {bars}
      </BarChart>
    );

    return (
      <Box sx={{ border: '1px solid #E4E5E7', borderRadius: 2, p: 2 }}>
        {title && (
          <Typography
            sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827', mb: 1 }}
          >
            {title}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          {scrollable && (
            <FixedYAxisScale
              height={height}
              axisHeight={axisHeight}
              yTicks={yTicks}
              yMax={yMax}
              label={yAxisLabel}
            />
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: scrollable ? height + 24 : height,
              overflowX: scrollable ? 'auto' : 'hidden',
              overflowY: 'hidden',
              scrollbarWidth: 'thin',
              scrollbarColor: '#CBD5E1 #F3F4F6',
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#F3F4F6',
                borderRadius: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#94A3B8',
                borderRadius: 8,
              },
            }}
          >
            {scrollable ? (
              plot
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {plot}
              </ResponsiveContainer>
            )}
          </Box>
          {scrollable && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 1.25,
                pl: 2,
                flexShrink: 0,
              }}
            >
              {series.map(seriesItem => (
                <Box
                  key={seriesItem.dataKey}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      backgroundColor: seriesItem.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: 14, color: '#374151' }}>
                    {seriesItem.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

StackedBarChart.displayName = 'StackedBarChart';

export default StackedBarChart;
